import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import actionViewIconUrl from '@/assets/action-menu/view.svg'
import { Badge } from '@/components/data-display/badge'
import { DataTable, type DataTableColumn } from '@/components/data-display/data-table'
import { DataTableColumnSelector } from '@/components/data-display/data-table-column-selector'
import { Pagination } from '@/components/data-display/pagination'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { CopyableReadonlyField } from '@/components/forms/copyable-readonly-field'
import { Input } from '@/components/forms/input'
import { SearchInput } from '@/components/forms/search-input'
import { Select, type SelectOption } from '@/components/forms/select'
import { PageHeader } from '@/components/layout/page-header'
import { ActionMenu } from '@/components/overlays/action-menu'
import { Drawer } from '@/components/overlays/drawer'
import { Button } from '@/components/primitives/button'
import { ProviderLogo } from '@/features/admin/model-provider/provider-logo'
import { formatDateTime } from '@/features/admin/shared/format'
import { useAdminListControls } from '@/features/admin/shared/use-admin-list-controls'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import { cn } from '@/lib/cn'
import { ApiError } from '@/lib/api/http-client'

import {
  getModelGatewayUsageOverview,
  listModelCallLogs,
  type ModelCallLog,
  type ModelCallLogFallbackFilter,
  type ModelCallLogSuccessFilter,
} from './api'

const pageSizeOptions = [10, 20, 50]
const defaultColumnIds = [
  'route',
  'provider',
  'status',
  'started-at',
  'source',
  'key',
  'latency',
  'tokens',
  'cost',
  'error',
  'actions',
]

const providerValues = [
  'openai',
  'anthropic',
  'gemini',
  'xai',
  'deepseek',
  'qwen',
  'kimi',
  'glm',
  'minimax',
] as const

const errorCategoryValues = [
  'auth_error',
  'expired_key',
  'stream_denied',
  'protocol_mismatch',
  'quota_exceeded',
  'validation_error',
  'model_not_allowed',
  'upstream_timeout',
  'upstream_connection_error',
  'upstream_invalid_response',
  'upstream_4xx',
  'upstream_5xx',
  'internal_error',
  'probe_failed',
] as const

const failureStageValues = [
  'auth',
  'model_resolution',
  'protocol_check',
  'quota_precheck',
  'upstream_dispatch',
  'upstream_decode',
  'stream_runtime',
  'internal',
  'probe',
  'internal_model_runtime',
  'internal_model_eval',
] as const

type OptionRecord = Record<string, string>
type TimeRangePreset = 'last24h' | 'last7d' | 'last30d' | 'custom'

interface TimeRangeState {
  preset: TimeRangePreset
  startedFrom: string
  startedTo: string
}

interface FloatingPosition {
  left: number
  top: number
  width: number
}

const defaultTimeRangePreset: Exclude<TimeRangePreset, 'custom'> = 'last7d'
const timeRangePresetHours: Record<Exclude<TimeRangePreset, 'custom'>, number> = {
  last24h: 24,
  last30d: 30 * 24,
  last7d: 7 * 24,
}
const floatingViewportPadding = 12
const timeRangePopoverWidth = 360
const timeRangePopoverEstimatedHeight = 356

function RefreshIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path
        d="M12.25 5.25A4.75 4.75 0 0 0 3.5 4.6M3.75 2.75v2.7h2.7m-2.7 5.3a4.75 4.75 0 0 0 8.75.65m-.25 1.85v-2.7h-2.7"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  )
}

function AdvancedFiltersIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path
        d="M3 4.25h10M5 8h6m-4 3.75h2"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.6"
      />
    </svg>
  )
}

function CalendarIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path
        d="M5 2.5v2m6-2v2M3.25 6.5h9.5m-9.5-1.25A1.75 1.75 0 0 1 5 3.5h6a1.75 1.75 0 0 1 1.75 1.75v7A1.75 1.75 0 0 1 11 14H5a1.75 1.75 0 0 1-1.75-1.75v-7Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  )
}

function padDatePart(value: number) {
  return String(value).padStart(2, '0')
}

function formatDateTimeLocalInput(value: Date) {
  return `${String(value.getFullYear())}-${padDatePart(value.getMonth() + 1)}-${padDatePart(
    value.getDate(),
  )}T${padDatePart(value.getHours())}:${padDatePart(value.getMinutes())}`
}

function createPresetTimeRange(preset: Exclude<TimeRangePreset, 'custom'>): TimeRangeState {
  const now = new Date()
  const startedFrom = new Date(now.getTime() - timeRangePresetHours[preset] * 60 * 60 * 1000)

  return {
    preset,
    startedFrom: formatDateTimeLocalInput(startedFrom),
    startedTo: formatDateTimeLocalInput(now),
  }
}

function formatDateTimeLocalText(value: string) {
  return value ? value.replace('T', ' ') : '-'
}

function getTimeRangePopoverPosition(triggerRect: DOMRect): FloatingPosition {
  const width = Math.min(timeRangePopoverWidth, Math.max(280, window.innerWidth - 24))
  const left = Math.min(
    window.innerWidth - width - floatingViewportPadding,
    Math.max(floatingViewportPadding, triggerRect.left),
  )
  const preferredTop = triggerRect.bottom + 8
  const top =
    preferredTop + timeRangePopoverEstimatedHeight > window.innerHeight
      ? Math.max(floatingViewportPadding, triggerRect.top - timeRangePopoverEstimatedHeight - 8)
      : preferredTop

  return { left, top, width }
}

function formatInteger(value?: number | null) {
  if (value == null) {
    return '-'
  }

  return new Intl.NumberFormat('zh-CN').format(value)
}

function formatCompactInteger(value?: number | null) {
  if (value == null) {
    return '-'
  }

  return new Intl.NumberFormat('zh-CN', {
    maximumFractionDigits: 1,
    notation: 'compact',
  }).format(value)
}

function formatLatency(value?: number | null) {
  if (value == null) {
    return '-'
  }

  if (value < 1000) {
    return `${String(value)}ms`
  }

  return `${(value / 1000).toFixed(2).replace(/\.00$/, '')}s`
}

function formatPercent(value?: number | null) {
  if (value == null) {
    return '-'
  }

  return `${(value * 100).toFixed(1)}%`
}

function currencyPrefix(currency: string) {
  switch (currency.toUpperCase()) {
    case 'CNY':
      return '¥'
    case 'USD':
      return '$'
    default:
      return `${currency} `
  }
}

function parseAmount(value?: number | string | null) {
  if (value == null || value === '') {
    return null
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function formatCostAmount(value: number | string | null | undefined, currency: string) {
  const amount = parseAmount(value)

  if (amount == null) {
    return '-'
  }

  return `${currencyPrefix(currency)}${amount
    .toFixed(amount >= 1 ? 2 : 6)
    .replace(/0+$/, '')
    .replace(/\.$/, '')}`
}

function formatSummaryCostAmount(value: number | string, currency: string) {
  const amount = parseAmount(value)

  if (amount == null) {
    return '-'
  }

  return `${currencyPrefix(currency)}${amount
    .toFixed(Math.min(3, amount >= 1 ? 2 : 3))
    .replace(/0+$/, '')
    .replace(/\.$/, '')}`
}

function formatSummaryCostByCurrency(costByCurrency: Record<string, number | string> | undefined) {
  const entries = Object.entries(costByCurrency ?? {})

  if (entries.length === 0) {
    return '-'
  }

  return entries
    .map(([currency, amount]) => formatSummaryCostAmount(amount, currency))
    .filter((item) => item !== '-')
    .join(' / ')
}

function formatModelRoute(log: ModelCallLog) {
  const finalModel = log.final_public_model_name ?? log.final_registered_model_name

  if (!finalModel || finalModel === log.requested_public_model_name) {
    return log.requested_public_model_name
  }

  return `${log.requested_public_model_name} -> ${finalModel}`
}

function formatSource(log: ModelCallLog) {
  if (!log.request_feature) {
    return log.request_source || '-'
  }

  return `${log.request_source || 'unknown'} / ${log.request_feature}`
}

function hasFallbackTriggered(log: ModelCallLog) {
  return log.fallback_pre_active || log.fallback_triggered_runtime
}

function formatLabel(value: string, labels: OptionRecord) {
  return labels[value] ?? value
}

function optionList(values: readonly string[], labels: OptionRecord): SelectOption[] {
  return values.map((value) => ({
    label: formatLabel(value, labels),
    value,
  }))
}

export function SummaryMetric({
  isLoading,
  label,
  value,
}: {
  isLoading?: boolean
  label: string
  value: string
}) {
  return (
    <div
      className="grid min-h-[112px] min-w-0 content-start gap-3 rounded-lg border border-[#ececec] bg-white p-5 text-left"
      data-testid="model-call-log-summary-card"
    >
      <span className="text-[15px] leading-5 tracking-[0.3px] text-[var(--color-text)]">
        {label}
      </span>
      {isLoading ? (
        <div
          className="h-8 w-20 animate-pulse rounded-full bg-[var(--color-skeleton)]"
          data-testid="model-call-log-summary-skeleton"
        />
      ) : (
        <div
          className="truncate text-[30px] leading-8 font-bold text-[var(--color-primary)]"
          title={value}
        >
          {value}
        </div>
      )}
    </div>
  )
}

function providerOptionList(values: readonly string[], labels: OptionRecord): SelectOption[] {
  return values.map((value) => ({
    icon: (
      <ProviderLogo
        className="size-5 rounded"
        label={formatLabel(value, labels)}
        provider={value}
        variant="compact"
      />
    ),
    label: formatLabel(value, labels),
    value,
  }))
}

function TimeRangeControl({
  onValueChange,
  value,
}: {
  onValueChange: (value: TimeRangeState) => void
  value: TimeRangeState
}) {
  const { t } = useTranslation('adminModelCallLogs')
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState<FloatingPosition | null>(null)
  const [draftFrom, setDraftFrom] = useState(value.startedFrom)
  const [draftTo, setDraftTo] = useState(value.startedTo)
  const presetLabels: Record<TimeRangePreset, string> = {
    custom: t('timeRange.presets.custom'),
    last24h: t('timeRange.presets.last24h'),
    last30d: t('timeRange.presets.last30d'),
    last7d: t('timeRange.presets.last7d'),
  }
  const triggerLabels: Record<TimeRangePreset, string> = {
    custom: t('timeRange.compactPresets.custom'),
    last24h: t('timeRange.compactPresets.last24h'),
    last30d: t('timeRange.compactPresets.last30d'),
    last7d: t('timeRange.compactPresets.last7d'),
  }
  const selectedLabel = triggerLabels[value.preset]
  const rangeTitle = t('timeRange.valueTitle', {
    from: formatDateTimeLocalText(value.startedFrom),
    to: formatDateTimeLocalText(value.startedTo),
  })

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') {
      return undefined
    }

    function updatePosition() {
      const trigger = triggerRef.current
      if (!trigger) {
        return
      }

      setPosition(getTimeRangePopoverPosition(trigger.getBoundingClientRect()))
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined
    }

    function closeOnOutsidePointer(event: PointerEvent) {
      const target = event.target

      if (!(target instanceof Node)) {
        return
      }

      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) {
        return
      }

      setIsOpen(false)
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('pointerdown', closeOnOutsidePointer)
    document.addEventListener('keydown', closeOnEscape)

    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [isOpen])

  function applyPreset(preset: Exclude<TimeRangePreset, 'custom'>) {
    onValueChange(createPresetTimeRange(preset))
    setIsOpen(false)
    triggerRef.current?.focus()
  }

  function applyCustomRange() {
    onValueChange({
      preset: 'custom',
      startedFrom: draftFrom,
      startedTo: draftTo,
    })
    setIsOpen(false)
    triggerRef.current?.focus()
  }

  return (
    <>
      <button
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={t('timeRange.ariaLabel')}
        className={cn(
          'flex h-[38px] w-full min-w-0 cursor-pointer items-center gap-2 rounded-lg border border-[var(--color-control-border)] bg-white px-3 text-left text-sm text-[var(--color-text)] shadow-[var(--shadow-control)] transition-colors outline-none',
          'hover:border-[var(--color-control-border-hover)] focus-visible:border-[var(--color-primary)] focus-visible:ring-2 focus-visible:ring-[var(--color-primary-soft)]',
        )}
        onClick={() => {
          if (!isOpen) {
            setDraftFrom(value.startedFrom)
            setDraftTo(value.startedTo)
          }

          setIsOpen(!isOpen)
        }}
        ref={triggerRef}
        title={rangeTitle}
        type="button"
      >
        <span className="shrink-0 text-[var(--color-text-muted)]">
          <CalendarIcon />
        </span>
        <span className="block min-w-0 flex-1 truncate">{selectedLabel}</span>
      </button>
      {isOpen && position && typeof document !== 'undefined'
        ? createPortal(
            <div
              aria-label={t('timeRange.dialogLabel')}
              className="fixed z-[120] rounded-lg border border-[var(--color-panel-border)] bg-white p-3 shadow-[0_16px_40px_rgba(15,23,42,0.16)]"
              ref={popoverRef}
              role="dialog"
              style={{ left: position.left, top: position.top, width: position.width }}
            >
              <div className="grid gap-3">
                <div className="grid gap-2">
                  <div className="text-xs font-medium text-[var(--color-text-muted)]">
                    {t('timeRange.quickRanges')}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {(['last24h', 'last7d', 'last30d'] as const).map((preset) => (
                      <button
                        aria-pressed={value.preset === preset}
                        className={cn(
                          'h-9 cursor-pointer rounded-md border px-2 text-sm font-medium transition-colors',
                          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                          value.preset === preset
                            ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)] text-[var(--color-primary)]'
                            : 'border-[var(--color-border)] bg-white text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]',
                        )}
                        key={preset}
                        onClick={() => applyPreset(preset)}
                        type="button"
                      >
                        {presetLabels[preset]}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid gap-3 rounded-lg bg-[var(--color-surface-muted)] p-3">
                  <div className="text-xs font-medium text-[var(--color-text-muted)]">
                    {t('timeRange.customRange')}
                  </div>
                  <div className="grid gap-2">
                    <label className="grid gap-1.5 text-xs font-medium text-[var(--color-text-muted)]">
                      {t('timeRange.from')}
                      <Input
                        aria-label={t('timeRange.from')}
                        onChange={(event) => setDraftFrom(event.target.value)}
                        type="datetime-local"
                        value={draftFrom}
                      />
                    </label>
                    <label className="grid gap-1.5 text-xs font-medium text-[var(--color-text-muted)]">
                      {t('timeRange.to')}
                      <Input
                        aria-label={t('timeRange.to')}
                        onChange={(event) => setDraftTo(event.target.value)}
                        type="datetime-local"
                        value={draftTo}
                      />
                    </label>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button onClick={() => setIsOpen(false)} size="sm" variant="secondary">
                    {t('timeRange.close')}
                  </Button>
                  <Button onClick={applyCustomRange} size="sm">
                    {t('timeRange.apply')}
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

function RequestIdCell({ value }: { value: string }) {
  return (
    <span
      className="block max-w-[150px] truncate font-mono text-xs text-[var(--color-text)]"
      title={value}
    >
      {value}
    </span>
  )
}

function TextCell({ className, value }: { className?: string; value?: string | null }) {
  const trimmed = value?.trim()
  const normalized = trimmed && trimmed.length > 0 ? trimmed : '-'

  return (
    <span
      className={cn('block max-w-[220px] truncate text-sm text-[var(--color-text)]', className)}
      title={normalized}
    >
      {normalized}
    </span>
  )
}

function ProviderCell({ provider }: { provider?: string | null }) {
  if (!provider) {
    return <span className="text-sm text-[var(--color-text-muted)]">-</span>
  }

  return (
    <span className="inline-flex max-w-[140px] items-center gap-1.5">
      <ProviderLogo
        className="size-5 rounded"
        label={provider}
        provider={provider}
        variant="compact"
      />
      <span className="truncate text-sm text-[var(--color-text)]" title={provider}>
        {provider}
      </span>
    </span>
  )
}

function ModelRouteCell({ log }: { log: ModelCallLog }) {
  const value = formatModelRoute(log)

  return (
    <span className="block max-w-[260px] truncate font-semibold text-[#1a1c19]" title={value}>
      {value}
    </span>
  )
}

function TokenCell({ log }: { log: ModelCallLog }) {
  return (
    <span
      className="font-mono text-xs text-[var(--color-text)]"
      title={formatInteger(log.total_tokens)}
    >
      {formatCompactInteger(log.total_tokens)}
    </span>
  )
}

function CostCell({ log }: { log: ModelCallLog }) {
  return (
    <span
      className="font-mono text-xs text-[var(--color-text)]"
      title={formatCostAmount(log.total_cost_amount, log.billing_currency)}
    >
      {formatCostAmount(log.total_cost_amount, log.billing_currency)}
    </span>
  )
}

function DetailRow({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <div className="grid gap-1">
      <dt className="text-xs font-medium tracking-normal text-[var(--color-text-muted)]">
        {label}
      </dt>
      <dd className="min-h-6 text-sm leading-6 break-words text-[var(--color-text)]">{children}</dd>
    </div>
  )
}

function DetailSection({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <section className="grid gap-4 rounded-lg border border-[var(--color-panel-border)] bg-white p-4">
      <h3 className="text-sm font-semibold text-[var(--color-text)]">{title}</h3>
      <dl className="grid gap-4 sm:grid-cols-2">{children}</dl>
    </section>
  )
}

function ModelCallLogDetailDrawer({
  log,
  onOpenChange,
  open,
}: {
  log: ModelCallLog
  onOpenChange: (open: boolean) => void
  open: boolean
}) {
  const { t } = useTranslation('adminModelCallLogs')
  const { showFeedback } = useFeedback()

  async function copyRequestId() {
    try {
      await navigator.clipboard.writeText(log.request_id)
      showFeedback({
        message: t('drawer.copySuccess'),
        scope: 'global',
        tone: 'success',
      })
    } catch {
      showFeedback({
        message: t('drawer.copyFailed'),
        scope: 'global',
        tone: 'error',
      })
    }
  }

  return (
    <Drawer
      bodyClassName="overflow-y-auto bg-[var(--color-surface-muted)] px-6 py-6"
      onOpenChange={onOpenChange}
      open={open}
      size="lg"
      title={t('drawer.title', { requestId: log.request_id })}
      titleMeta={log.success ? t('status.success') : t('status.failure')}
    >
      <div className="grid gap-4">
        <section className="rounded-lg border border-[var(--color-panel-border)] bg-white p-4">
          <CopyableReadonlyField
            copyLabel={t('drawer.copyRequestId')}
            label={t('drawer.requestId')}
            monospace
            onCopy={() => void copyRequestId()}
            showLabel
            value={log.request_id}
          />
        </section>
        <DetailSection title={t('drawer.sections.basic')}>
          <DetailRow label={t('table.startedAt')}>{formatDateTime(log.started_at)}</DetailRow>
          <DetailRow label={t('drawer.finishedAt')}>{formatDateTime(log.finished_at)}</DetailRow>
          <DetailRow label={t('table.status')}>
            <Badge variant={log.success ? 'success' : 'danger'}>
              {log.success ? t('status.success') : t('status.failure')}
            </Badge>
          </DetailRow>
          <DetailRow label={t('table.statusCode')}>{String(log.status_code)}</DetailRow>
          <DetailRow label={t('table.latency')}>{formatLatency(log.latency_ms)}</DetailRow>
          <DetailRow label={t('table.source')}>{formatSource(log)}</DetailRow>
        </DetailSection>
        <DetailSection title={t('drawer.sections.route')}>
          <DetailRow label={t('drawer.requestedModel')}>
            {log.requested_public_model_name}
          </DetailRow>
          <DetailRow label={t('drawer.resolvedModel')}>
            {log.resolved_public_model_name ?? log.resolved_registered_model_name ?? '-'}
          </DetailRow>
          <DetailRow label={t('drawer.finalModel')}>
            {log.final_public_model_name ?? log.final_registered_model_name ?? '-'}
          </DetailRow>
          <DetailRow label={t('table.provider')}>{log.upstream_provider ?? '-'}</DetailRow>
          <DetailRow label={t('drawer.interfaceType')}>
            {log.upstream_interface_type ?? '-'}
          </DetailRow>
          <DetailRow label={t('drawer.outputProtocol')}>{log.output_protocol}</DetailRow>
        </DetailSection>
        <DetailSection title={t('drawer.sections.key')}>
          <DetailRow label={t('table.key')}>{log.distribution_key_name}</DetailRow>
          <DetailRow label={t('drawer.keyId')}>{log.distribution_key_id ?? '-'}</DetailRow>
          <DetailRow label={t('drawer.routeKind')}>{log.route_kind}</DetailRow>
          <DetailRow label={t('drawer.pricingSource')}>{log.pricing_source}</DetailRow>
        </DetailSection>
        <DetailSection title={t('drawer.sections.usage')}>
          <DetailRow label={t('drawer.promptTokens')}>{formatInteger(log.prompt_tokens)}</DetailRow>
          <DetailRow label={t('drawer.completionTokens')}>
            {formatInteger(log.completion_tokens)}
          </DetailRow>
          <DetailRow label={t('table.tokens')}>{formatInteger(log.total_tokens)}</DetailRow>
          <DetailRow label={t('drawer.inputCost')}>
            {formatCostAmount(log.input_cost_amount, log.billing_currency)}
          </DetailRow>
          <DetailRow label={t('drawer.outputCost')}>
            {formatCostAmount(log.output_cost_amount, log.billing_currency)}
          </DetailRow>
          <DetailRow label={t('table.cost')}>
            {formatCostAmount(log.total_cost_amount, log.billing_currency)}
          </DetailRow>
        </DetailSection>
        <DetailSection title={t('drawer.sections.controls')}>
          <DetailRow label={t('table.fallback')}>
            <Badge variant={hasFallbackTriggered(log) ? 'warning' : 'neutral'}>
              {hasFallbackTriggered(log) ? t('fallback.triggered') : t('fallback.none')}
            </Badge>
          </DetailRow>
          <DetailRow label={t('drawer.fallbackReason')}>{log.fallback_reason ?? '-'}</DetailRow>
          <DetailRow label={t('table.quota')}>
            <Badge variant={log.quota_rule_triggered ? 'warning' : 'neutral'}>
              {log.quota_rule_triggered ? t('quota.triggered') : t('quota.none')}
            </Badge>
          </DetailRow>
          <DetailRow label={t('drawer.quotaRule')}>{log.quota_rule_id ?? '-'}</DetailRow>
          <DetailRow label={t('drawer.quotaMetric')}>{log.quota_rule_metric ?? '-'}</DetailRow>
          <DetailRow label={t('drawer.quotaAction')}>{log.quota_rule_action ?? '-'}</DetailRow>
          <DetailRow label={t('drawer.quotaMessage')}>{log.quota_rule_message ?? '-'}</DetailRow>
        </DetailSection>
        <DetailSection title={t('drawer.sections.failure')}>
          <DetailRow label={t('table.errorCategory')}>{log.error_category ?? '-'}</DetailRow>
          <DetailRow label={t('table.failureStage')}>{log.failure_stage ?? '-'}</DetailRow>
          <DetailRow label={t('drawer.errorMessage')}>
            <span className="whitespace-pre-wrap">{log.error_message ?? '-'}</span>
          </DetailRow>
        </DetailSection>
      </div>
    </Drawer>
  )
}

function AdvancedFiltersDrawer({
  distributionKeyId,
  errorCategory,
  errorCategoryOptions,
  failureStage,
  failureStageOptions,
  fallbackOptions,
  fallbackTriggered,
  onClear,
  onClose,
  onDistributionKeyIdChange,
  onErrorCategoryChange,
  onFailureStageChange,
  onFallbackTriggeredChange,
  onRequestIdChange,
  open,
  requestId,
}: {
  distributionKeyId: string
  errorCategory: string
  errorCategoryOptions: SelectOption[]
  failureStage: string
  failureStageOptions: SelectOption[]
  fallbackOptions: SelectOption[]
  fallbackTriggered: ModelCallLogFallbackFilter
  onClear: () => void
  onClose: () => void
  onDistributionKeyIdChange: (value: string) => void
  onErrorCategoryChange: (value: string) => void
  onFailureStageChange: (value: string) => void
  onFallbackTriggeredChange: (value: ModelCallLogFallbackFilter) => void
  onRequestIdChange: (value: string) => void
  open: boolean
  requestId: string
}) {
  const { t } = useTranslation('adminModelCallLogs')

  return (
    <Drawer
      bodyClassName="overflow-y-auto px-6 py-6"
      footer={
        <>
          <Button onClick={onClear} size="sm" variant="secondary">
            {t('filters.clearAdvanced')}
          </Button>
          <Button onClick={onClose} size="sm">
            {t('advancedFilters.done')}
          </Button>
        </>
      }
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose()
        }
      }}
      open={open}
      size="md"
      title={t('advancedFilters.title')}
    >
      <div className="grid gap-4">
        <div className="grid gap-1.5">
          <span className="text-sm font-medium text-[var(--color-text)]">
            {t('filters.requestIdLabel')}
          </span>
          <SearchInput
            aria-label={t('filters.requestIdLabel')}
            onChange={(event) => onRequestIdChange(event.target.value)}
            placeholder={t('filters.requestIdPlaceholder')}
            value={requestId}
          />
        </div>
        <div className="grid gap-1.5">
          <span className="text-sm font-medium text-[var(--color-text)]">
            {t('filters.keyIdLabel')}
          </span>
          <Input
            aria-label={t('filters.keyIdLabel')}
            onChange={(event) => onDistributionKeyIdChange(event.target.value)}
            placeholder={t('filters.keyIdPlaceholder')}
            value={distributionKeyId}
          />
        </div>
        <div className="grid gap-1.5">
          <span className="text-sm font-medium text-[var(--color-text)]">
            {t('filters.fallbackLabel')}
          </span>
          <Select
            aria-label={t('filters.fallbackLabel')}
            onValueChange={(value) =>
              onFallbackTriggeredChange(value as ModelCallLogFallbackFilter)
            }
            options={fallbackOptions}
            value={fallbackTriggered}
          />
        </div>
        <div className="grid gap-1.5">
          <span className="text-sm font-medium text-[var(--color-text)]">
            {t('filters.errorCategoryLabel')}
          </span>
          <Select
            aria-label={t('filters.errorCategoryLabel')}
            onValueChange={onErrorCategoryChange}
            options={errorCategoryOptions}
            value={errorCategory}
          />
        </div>
        <div className="grid gap-1.5">
          <span className="text-sm font-medium text-[var(--color-text)]">
            {t('filters.failureStageLabel')}
          </span>
          <Select
            aria-label={t('filters.failureStageLabel')}
            onValueChange={onFailureStageChange}
            options={failureStageOptions}
            value={failureStage}
          />
        </div>
      </div>
    </Drawer>
  )
}

export function AdminModelCallLogsPage() {
  const { t } = useTranslation('adminModelCallLogs')
  const {
    page,
    pageSize,
    search,
    searchInput,
    setPage,
    setPageSize,
    setSearchInput,
    setVisibleColumnIds,
    visibleColumnIds,
    visibleColumnIdSet,
  } = useAdminListControls({ defaultColumnIds, defaultPageSize: 20 })
  const [timeRange, setTimeRange] = useState<TimeRangeState>(() =>
    createPresetTimeRange(defaultTimeRangePreset),
  )
  const [success, setSuccess] = useState<ModelCallLogSuccessFilter>('')
  const [provider, setProvider] = useState('')
  const [requestedModel, setRequestedModel] = useState('')
  const [distributionKeyId, setDistributionKeyId] = useState('')
  const [fallbackTriggered, setFallbackTriggered] = useState<ModelCallLogFallbackFilter>('')
  const [errorCategory, setErrorCategory] = useState('')
  const [failureStage, setFailureStage] = useState('')
  const [selectedLog, setSelectedLog] = useState<ModelCallLog | null>(null)
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false)
  const startedFrom = timeRange.startedFrom
  const startedTo = timeRange.startedTo

  const listQuery = useQuery({
    placeholderData: (previous) => previous,
    queryFn: () =>
      listModelCallLogs({
        distributionKeyId,
        errorCategory,
        failureStage,
        fallbackTriggered,
        page,
        pageSize,
        provider,
        requestId: search,
        requestedPublicModelName: requestedModel,
        startedFrom,
        startedTo,
        success,
      }),
    queryKey: [
      'admin-model-call-logs',
      {
        distributionKeyId,
        errorCategory,
        failureStage,
        fallbackTriggered,
        page,
        pageSize,
        provider,
        requestedModel,
        search,
        startedFrom,
        startedTo,
        success,
      },
    ],
  })

  const overviewQuery = useQuery({
    queryFn: () =>
      getModelGatewayUsageOverview({
        startedFrom,
        startedTo,
      }),
    queryKey: ['admin-model-gateway-usage-overview', { startedFrom, startedTo }],
  })

  const statusOptions: SelectOption[] = useMemo(
    () => [
      { label: t('filters.allStatuses'), value: '' },
      { label: t('status.success'), value: 'true' },
      { label: t('status.failure'), value: 'false' },
    ],
    [t],
  )
  const fallbackOptions: SelectOption[] = useMemo(
    () => [
      { label: t('filters.allFallback'), value: '' },
      { label: t('fallback.triggered'), value: 'true' },
    ],
    [t],
  )
  const providerOptions: SelectOption[] = useMemo(
    () => [
      { label: t('filters.allProviders'), value: '' },
      ...providerOptionList(
        providerValues,
        t('providers', { returnObjects: true }) as OptionRecord,
      ),
    ],
    [t],
  )
  const errorCategoryOptions: SelectOption[] = useMemo(
    () => [
      { label: t('filters.allErrorCategories'), value: '' },
      ...optionList(
        errorCategoryValues,
        t('errorCategories', { returnObjects: true }) as OptionRecord,
      ),
    ],
    [t],
  )
  const failureStageOptions: SelectOption[] = useMemo(
    () => [
      { label: t('filters.allFailureStages'), value: '' },
      ...optionList(
        failureStageValues,
        t('failureStages', { returnObjects: true }) as OptionRecord,
      ),
    ],
    [t],
  )

  const columns = useMemo<DataTableColumn<ModelCallLog>[]>(
    () => [
      {
        header: t('table.requestId'),
        hidden: !visibleColumnIdSet.has('request-id'),
        id: 'request-id',
        pin: 'left',
        render: (log) => <RequestIdCell value={log.request_id} />,
        visibilityLabel: t('table.requestId'),
        width: 170,
      },
      {
        header: t('table.route'),
        hidden: !visibleColumnIdSet.has('route'),
        id: 'route',
        pin: 'left',
        render: (log) => <ModelRouteCell log={log} />,
        visibilityLabel: t('table.route'),
        width: 285,
      },
      {
        header: t('table.provider'),
        hidden: !visibleColumnIdSet.has('provider'),
        id: 'provider',
        render: (log) => <ProviderCell provider={log.upstream_provider} />,
        visibilityLabel: t('table.provider'),
        width: 150,
      },
      {
        header: t('table.status'),
        hidden: !visibleColumnIdSet.has('status'),
        id: 'status',
        render: (log) => (
          <Badge variant={log.success ? 'success' : 'danger'}>
            {log.success ? t('status.success') : t('status.failure')}
          </Badge>
        ),
        visibilityLabel: t('table.status'),
        width: 90,
      },
      {
        header: t('table.startedAt'),
        hidden: !visibleColumnIdSet.has('started-at'),
        id: 'started-at',
        render: (log) => formatDateTime(log.started_at),
        visibilityLabel: t('table.startedAt'),
        width: 165,
      },
      {
        header: t('table.source'),
        hidden: !visibleColumnIdSet.has('source'),
        id: 'source',
        render: (log) => <TextCell className="max-w-[180px]" value={formatSource(log)} />,
        visibilityLabel: t('table.source'),
        width: 210,
      },
      {
        header: t('table.key'),
        hidden: !visibleColumnIdSet.has('key'),
        id: 'key',
        render: (log) => <TextCell className="max-w-[180px]" value={log.distribution_key_name} />,
        visibilityLabel: t('table.key'),
        width: 210,
      },
      {
        align: 'right',
        header: t('table.latency'),
        hidden: !visibleColumnIdSet.has('latency'),
        id: 'latency',
        render: (log) => <span className="font-mono text-xs">{formatLatency(log.latency_ms)}</span>,
        visibilityLabel: t('table.latency'),
        width: 105,
      },
      {
        align: 'right',
        header: t('table.tokens'),
        hidden: !visibleColumnIdSet.has('tokens'),
        id: 'tokens',
        render: (log) => <TokenCell log={log} />,
        visibilityLabel: t('table.tokens'),
        width: 110,
      },
      {
        align: 'right',
        header: t('table.cost'),
        hidden: !visibleColumnIdSet.has('cost'),
        id: 'cost',
        render: (log) => <CostCell log={log} />,
        visibilityLabel: t('table.cost'),
        width: 125,
      },
      {
        header: t('table.error'),
        hidden: !visibleColumnIdSet.has('error'),
        id: 'error',
        render: (log) => (
          <TextCell
            className="max-w-[210px]"
            value={
              log.error_category
                ? `${log.error_category}${log.failure_stage ? ` / ${log.failure_stage}` : ''}`
                : '-'
            }
          />
        ),
        visibilityLabel: t('table.error'),
        width: 230,
      },
      {
        header: t('table.fallback'),
        hidden: !visibleColumnIdSet.has('fallback'),
        id: 'fallback',
        render: (log) => (
          <Badge variant={hasFallbackTriggered(log) ? 'warning' : 'neutral'}>
            {hasFallbackTriggered(log) ? t('fallback.triggered') : t('fallback.none')}
          </Badge>
        ),
        visibilityLabel: t('table.fallback'),
        width: 105,
      },
      {
        header: t('table.quota'),
        hidden: !visibleColumnIdSet.has('quota'),
        id: 'quota',
        render: (log) => (
          <Badge variant={log.quota_rule_triggered ? 'warning' : 'neutral'}>
            {log.quota_rule_triggered ? t('quota.triggered') : t('quota.none')}
          </Badge>
        ),
        visibilityLabel: t('table.quota'),
        width: 100,
      },
      {
        align: 'center',
        header: t('table.actions'),
        hidden: !visibleColumnIdSet.has('actions'),
        id: 'actions',
        pin: 'right',
        render: (log) => (
          <ActionMenu
            ariaLabel={t('actions.open', { requestId: log.request_id })}
            items={[
              {
                iconSrc: actionViewIconUrl,
                key: 'view',
                label: t('actions.view'),
                onSelect: () => setSelectedLog(log),
              },
            ]}
          />
        ),
        visibilityLabel: t('table.actions'),
        width: 70,
      },
    ],
    [t, visibleColumnIdSet],
  )

  const columnSelectorOptions = useMemo(
    () =>
      columns.map((column) => ({
        id: column.id,
        label: column.visibilityLabel ?? column.id,
      })),
    [columns],
  )

  const logs = listQuery.data?.items ?? []
  const total = listQuery.data?.total ?? 0
  const overview = overviewQuery.data
  const showError = listQuery.isError && !listQuery.data
  const errorDescription =
    listQuery.error instanceof ApiError
      ? t('errorDescriptionWithDetail', { detail: listQuery.error.message })
      : t('errorDescription')
  const hasActiveFilters = Boolean(
    timeRange.preset !== defaultTimeRangePreset ||
    searchInput ||
    success ||
    provider ||
    requestedModel ||
    distributionKeyId ||
    fallbackTriggered ||
    errorCategory ||
    failureStage,
  )
  const activeAdvancedFilterCount = [
    searchInput,
    distributionKeyId,
    fallbackTriggered,
    errorCategory,
    failureStage,
  ].filter((value) => value.trim().length > 0).length

  function clearFilters() {
    setTimeRange(createPresetTimeRange(defaultTimeRangePreset))
    setSearchInput('')
    setSuccess('')
    setProvider('')
    setRequestedModel('')
    setDistributionKeyId('')
    setFallbackTriggered('')
    setErrorCategory('')
    setFailureStage('')
    setPage(1)
  }

  function clearAdvancedFilters() {
    setSearchInput('')
    setDistributionKeyId('')
    setFallbackTriggered('')
    setErrorCategory('')
    setFailureStage('')
    setPage(1)
  }

  return (
    <AdminShellPage activePath="/admin/model-call-logs">
      <section
        aria-label={t('workspaceLabel')}
        className="flex h-full min-w-0 flex-col overflow-hidden"
        data-testid="admin-model-call-logs-workspace"
      >
        <PageHeader
          actions={
            <>
              <DataTableColumnSelector
                aria-label={t('columnSelector.label')}
                onValueChange={setVisibleColumnIds}
                options={columnSelectorOptions}
                value={visibleColumnIds}
              >
                {t('columnSelector.trigger')}
              </DataTableColumnSelector>
              <Button
                isLoading={listQuery.isFetching || overviewQuery.isFetching}
                onClick={() => {
                  void listQuery.refetch()
                  void overviewQuery.refetch()
                }}
                size="sm"
                variant="secondary"
              >
                <RefreshIcon />
                {t('actions.refresh')}
              </Button>
            </>
          }
          description={t('description')}
          title={t('title')}
        />
        <div className="px-5 pb-4 lg:px-[30px]">
          <div className="w-full max-w-[1120px] overflow-x-auto pb-1">
            <div className="grid min-w-[640px] grid-cols-5 gap-2.5 sm:min-w-[680px] lg:gap-3">
              <SummaryMetric
                isLoading={overviewQuery.isLoading}
                label={t('summary.requests')}
                value={formatInteger(overview?.request_count)}
              />
              <SummaryMetric
                isLoading={overviewQuery.isLoading}
                label={t('summary.successRate')}
                value={formatPercent(overview?.success_rate)}
              />
              <SummaryMetric
                isLoading={overviewQuery.isLoading}
                label={t('summary.avgLatency')}
                value={formatLatency(Math.round(overview?.avg_latency_ms ?? 0))}
              />
              <SummaryMetric
                isLoading={overviewQuery.isLoading}
                label={t('summary.totalTokens')}
                value={formatCompactInteger(overview?.total_tokens)}
              />
              <SummaryMetric
                isLoading={overviewQuery.isLoading}
                label={t('summary.cost')}
                value={formatSummaryCostByCurrency(overview?.cost_by_currency)}
              />
            </div>
          </div>
        </div>
        <div className="relative px-5 pt-0 pb-4 lg:px-[30px]">
          <div className="flex w-full max-w-[1120px] flex-wrap items-center gap-3">
            <SearchInput
              aria-label={t('filters.modelLabel')}
              className="min-w-[200px] max-w-[300px] flex-[0_1_260px]"
              onChange={(event) => {
                setRequestedModel(event.target.value)
                setPage(1)
              }}
              placeholder={t('filters.modelPlaceholder')}
              value={requestedModel}
            />
            <Select
              aria-label={t('filters.providerLabel')}
              className="min-w-[160px] max-w-[200px] flex-[0_1_190px]"
              onValueChange={(value) => {
                setProvider(value)
                setPage(1)
              }}
              options={providerOptions}
              value={provider}
            />
            <Select
              aria-label={t('filters.statusLabel')}
              className="min-w-[140px] max-w-[170px] flex-[0_1_160px]"
              onValueChange={(value) => {
                setSuccess(value as ModelCallLogSuccessFilter)
                setPage(1)
              }}
              options={statusOptions}
              value={success}
            />
            <div className="min-w-[128px] max-w-[158px] flex-[0_1_148px]">
              <TimeRangeControl
                onValueChange={(nextRange) => {
                  setTimeRange(nextRange)
                  setPage(1)
                }}
                value={timeRange}
              />
            </div>
            <div className="flex w-full max-w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-[0_1_auto]">
              <Button
                aria-label={t('filters.advancedLabel', { count: activeAdvancedFilterCount })}
                onClick={() => setAdvancedFiltersOpen(true)}
                size="sm"
                variant={activeAdvancedFilterCount > 0 ? 'soft' : 'secondary'}
              >
                <AdvancedFiltersIcon />
                {t('filters.advanced')}
                {activeAdvancedFilterCount > 0 ? (
                  <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-white px-1.5 text-xs leading-5 font-semibold text-[var(--color-primary)]">
                    {activeAdvancedFilterCount}
                  </span>
                ) : null}
              </Button>
              <Button
                disabled={!hasActiveFilters}
                onClick={clearFilters}
                size="sm"
                variant="secondary"
              >
                {t('filters.clear')}
              </Button>
            </div>
          </div>
          <div
            aria-hidden
            className="pointer-events-none absolute right-5 bottom-0 left-5 h-px bg-[var(--color-divider-subtle)] lg:right-[30px] lg:left-[30px]"
          />
        </div>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4 lg:px-[30px]">
          {showError ? (
            <ErrorState
              action={
                <Button
                  className="mt-3"
                  onClick={() => void listQuery.refetch()}
                  size="sm"
                  variant="secondary"
                >
                  {t('retry')}
                </Button>
              }
              description={errorDescription}
              title={t('errorTitle')}
            />
          ) : (
            <>
              <DataTable
                columns={columns}
                empty={t('empty')}
                getRowKey={(log) => log.id}
                isLoading={listQuery.isLoading}
                rows={logs}
              />
              <Pagination
                disabled={listQuery.isFetching}
                labels={{
                  next: t('pagination.next'),
                  pageSize: (size) => t('pagination.pageSize', { count: size }),
                  previous: t('pagination.previous'),
                }}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                page={page}
                pageSize={pageSize}
                pageSizeOptions={pageSizeOptions}
                total={total}
              />
            </>
          )}
        </div>
        {selectedLog ? (
          <ModelCallLogDetailDrawer
            log={selectedLog}
            onOpenChange={(open) => {
              if (!open) {
                setSelectedLog(null)
              }
            }}
            open={Boolean(selectedLog)}
          />
        ) : null}
        <AdvancedFiltersDrawer
          distributionKeyId={distributionKeyId}
          errorCategory={errorCategory}
          errorCategoryOptions={errorCategoryOptions}
          failureStage={failureStage}
          failureStageOptions={failureStageOptions}
          fallbackOptions={fallbackOptions}
          fallbackTriggered={fallbackTriggered}
          onClear={clearAdvancedFilters}
          onClose={() => setAdvancedFiltersOpen(false)}
          onDistributionKeyIdChange={(value) => {
            setDistributionKeyId(value)
            setPage(1)
          }}
          onErrorCategoryChange={(value) => {
            setErrorCategory(value)
            setPage(1)
          }}
          onFailureStageChange={(value) => {
            setFailureStage(value)
            setPage(1)
          }}
          onFallbackTriggeredChange={(value) => {
            setFallbackTriggered(value)
            setPage(1)
          }}
          onRequestIdChange={(value) => {
            setSearchInput(value)
          }}
          open={advancedFiltersOpen}
          requestId={searchInput}
        />
      </section>
    </AdminShellPage>
  )
}
